<template>
  <div>
    <!-- キー選択 -->
    <div class="mb-3 key-selection">
      <span class="text-14 text-gray-700 mr-2">比較キー: </span>
      <el-select
        v-model="keyType"
        size="small"
        style="width: 250px"
        @change="handleKeyChange"
      >
        <el-option label="商品名" value="goodsShortName" />
        <el-option label="商品コード" value="goodsRefNo1" />
        <el-option label="商品名 + 単価" value="goodsShortName_unitPrice" />
        <el-option label="商品名 + 原産国" value="goodsShortName_origin" />
        <el-option label="商品名 + 商品コード" value="goodsShortName_goodsRefNo1" />
      </el-select>
    </div>

    <!-- 明細項目数の表示 -->
    <div class="mb-3 text-14 text-gray-800">
      <span>明細項目総数: </span>
      <span
        v-for="(doc, index) in selectedDocuments"
        :key="index"
        class="ml-3"
      >
        <span class="text-bold">{{ docLabels[index] }}:</span>
        {{ getDocumentItemCount(index) }}件
      </span>
    </div>

    <!-- 説明 -->
    <div class="mb-3 text-12 text-gray-600">
      ※ {{ baseDocLabel }}の明細から行を選択すると、選択したキーに基づいて他の書類から一致する明細を検索します。
    </div>

    <!-- 書類1の明細テーブル -->
    <div class="base-table-container mb-4">
      <div class="section-header">
        <h4 class="table-title">{{ baseDocLabel }}の明細（{{ selectedDocuments[0] && selectedDocuments[0].documentName || '' }}）</h4>
      </div>
      <el-table
        :data="baseItems"
        border
        stripe
        size="small"
        style="width: 100%"
        highlight-current-row
        :row-class-name="getBaseRowClassName"
        @current-change="handleBaseRowSelect"
      >
        <el-table-column
          prop="lineNo"
          label="明細No"
          width="80"
          align="center"
        />
        <el-table-column
          label="商品名"
          width="200"
          align="left"
          :label-class-name="isKeyColumnHighlighted('goodsShortName') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            <span style="white-space: normal; word-break: break-word; display: block;">
              {{ row.ocrGoodsShortName || '( no result )' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          label="商品コード"
          width="150"
          align="left"
          :label-class-name="isKeyColumnHighlighted('goodsRefNo1') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            {{ row.ocrGoodsRefNo1 || '( no result )' }}
          </template>
        </el-table-column>
        <el-table-column
          label="原産国"
          width="120"
          align="left"
          :label-class-name="isKeyColumnHighlighted('origin') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            {{ row.ocrCountryOfOriginNames || '( no result )' }}
          </template>
        </el-table-column>
        <el-table-column label="数量" width="120" align="right">
          <template slot-scope="{ row }">
            {{ formatQuantity(row) }}
          </template>
        </el-table-column>
        <el-table-column
          label="単価"
          width="120"
          align="right"
          :label-class-name="isKeyColumnHighlighted('unitPrice') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            {{ formatPrice(row) }}
          </template>
        </el-table-column>
        <el-table-column label="金額" width="120" align="right">
          <template slot-scope="{ row }">
            {{ formatAmount(row) }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- マッチした明細のセクション -->
    <div
      v-if="selectedBaseRow && matchedItems.length > 0"
      v-loading="isRecalculating"
      element-loading-text="再計算中..."
      element-loading-spinner="el-icon-loading"
      class="match-result-container"
    >
      <div class="section-header">
        <h4 class="table-title">
          マッチした明細（明細No: {{ selectedBaseRow.lineNo }}、
          キー: {{ getItemKey(selectedBaseRow, keyType) || '( no result )' }}）
        </h4>
        <el-button
          type="normal"
          size="small"
          icon="el-icon-download"
          @click="downloadMatchedItemsTsv"
        >
          TSVダウンロード
        </el-button>
      </div>
      <el-table
        :key="matchedTableKey"
        :data="matchedItems"
        border
        stripe
        size="small"
        style="width: 100%"
        show-summary
        :summary-method="getMatchedItemsSummaries"
        class="matched-items-table"
      >
        <el-table-column label="書類No" width="80" align="center">
          <template slot-scope="{ row }">
            {{ getDocumentNumber(row.documentName) }}
          </template>
        </el-table-column>
        <el-table-column label="ファイル名" width="220" align="left">
          <template slot-scope="{ row }">
            <span class="text-12">{{ row.documentName }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="lineNo"
          label="明細No"
          width="80"
          align="center"
        />
        <el-table-column
          label="商品名"
          width="200"
          align="left"
          :label-class-name="isKeyColumnHighlighted('goodsShortName') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            <span style="white-space: normal; word-break: break-word; display: block;">
              {{ row.ocrGoodsShortName || '( no result )' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          label="商品コード"
          width="150"
          align="left"
          :label-class-name="isKeyColumnHighlighted('goodsRefNo1') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            {{ row.ocrGoodsRefNo1 || '( no result )' }}
          </template>
        </el-table-column>
        <el-table-column
          label="原産国"
          width="120"
          align="left"
          :label-class-name="isKeyColumnHighlighted('origin') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            {{ row.ocrCountryOfOriginNames || '( no result )' }}
          </template>
        </el-table-column>
        <el-table-column label="数量" width="120" align="right">
          <template slot-scope="{ row }">
            {{ formatQuantity(row) }}
          </template>
        </el-table-column>
        <el-table-column
          label="単価"
          width="120"
          align="right"
          :label-class-name="isKeyColumnHighlighted('unitPrice') ? 'key-column-header' : ''"
        >
          <template slot-scope="{ row }">
            {{ formatPrice(row) }}
          </template>
        </el-table-column>
        <el-table-column label="金額" width="120" align="right">
          <template slot-scope="{ row }">
            {{ formatAmount(row) }}
          </template>
        </el-table-column>
        <el-table-column label="判定" width="60" align="center">
          <template slot-scope="{ row }">
            <el-tag
              :type="getMatchedItemJudgment(row) === 'OK' ? 'success' : 'danger'"
              size="small"
            >
              {{ getMatchedItemJudgment(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="差分（数量、金額を除く）" width="250" align="left">
          <template slot-scope="{ row }">
            <div style="white-space: pre-line;">
              {{ getMatchedItemDifference(row) }}
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- マッチしない明細のセクション -->
    <div
      v-if="selectedBaseRow && unmatchedItemsByDoc.length > 0"
      v-loading="isRecalculating"
      element-loading-text="再計算中..."
      element-loading-spinner="el-icon-loading"
      class="unmatched-result-container"
    >
      <div class="section-header">
        <h4 class="table-title">マッチしない明細</h4>
        <el-button
          type="normal"
          size="small"
          icon="el-icon-download"
          @click="downloadUnmatchedItemsTsv"
        >
          TSVダウンロード
        </el-button>
      </div>

      <!-- マッチしない明細の詳細 -->
      <div class="unmatched-items-details">
        <el-table
          :data="unmatchedItemsFlat"
          border
          stripe
          size="small"
          style="width: 100%"
        >
          <el-table-column label="書類No" width="80" align="center">
            <template slot-scope="{ row }">
              {{ getDocumentNumber(row.documentName) }}
            </template>
          </el-table-column>
          <el-table-column label="ファイル名" width="220" align="left">
            <template slot-scope="{ row }">
              <span class="text-12">{{ row.documentName }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="lineNo"
            label="明細No"
            width="80"
            align="center"
          />
          <el-table-column
            label="商品名"
            width="200"
            align="left"
            :label-class-name="isKeyColumnHighlighted('goodsShortName') ? 'key-column-header' : ''"
          >
            <template slot-scope="{ row }">
              <span
                style="white-space: normal; word-break: break-word; display: block;"
              >
                {{ row.ocrGoodsShortName || '( no result )' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            label="商品コード"
            width="150"
            align="left"
            :label-class-name="isKeyColumnHighlighted('goodsRefNo1') ? 'key-column-header' : ''"
          >
            <template slot-scope="{ row }">
              {{ row.ocrGoodsRefNo1 || '( no result )' }}
            </template>
          </el-table-column>
          <el-table-column
            label="原産国"
            width="120"
            align="left"
            :label-class-name="isKeyColumnHighlighted('origin') ? 'key-column-header' : ''"
          >
            <template slot-scope="{ row }">
              {{ row.ocrCountryOfOriginNames || '( no result )' }}
            </template>
          </el-table-column>
          <el-table-column label="数量" width="120" align="right">
            <template slot-scope="{ row }">
              {{ formatQuantity(row) }}
            </template>
          </el-table-column>
          <el-table-column
            label="単価"
            width="120"
            align="right"
            :label-class-name="isKeyColumnHighlighted('unitPrice') ? 'key-column-header' : ''"
          >
            <template slot-scope="{ row }">
              {{ formatPrice(row) }}
            </template>
          </el-table-column>
          <el-table-column label="金額" width="120" align="right">
            <template slot-scope="{ row }">
              {{ formatAmount(row) }}
            </template>
          </el-table-column>
          <el-table-column label="判定" width="60" align="center">
            <template>
              <el-tag type="danger" size="small">NG</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="差分（数量、金額を除く）" width="250" align="left">
            <template slot-scope="{ row }">
              <div>{{ getUnmatchedItemDifference(row) }}</div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- マッチなしメッセージ -->
    <div
      v-if="selectedBaseRow && matchedItems.length === 0 && unmatchedDocuments.length === 0"
      class="no-match-message"
    >
      <i class="el-icon-warning text-warning"></i>
      マッチする明細が見つかりませんでした
    </div>

    <!-- 未選択メッセージ -->
    <div v-if="!selectedBaseRow" class="no-selection-message">
      <i class="el-icon-info text-info"></i>
      {{ baseDocLabel }}の明細から行を選択してください
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrossCheckItemsMultiComparison',
  props: {
    compareData: {
      type: Object,
      default: null,
    },
    selectedDocuments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      keyType: 'goodsShortName',
      baseItems: [],
      selectedBaseRow: null,
      matchedItems: [],
      unmatchedDocuments: [],
      unmatchedItemsByDoc: [],
      isRecalculating: false,
    };
  },
  watch: {
    compareData: {
      handler(newVal) {
        if (newVal && newVal.documents) {
          this.loadBaseItems();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    docLabels() {
      const counts = {};
      this.selectedDocuments.forEach((doc) => {
        const cat = doc.documentCategory || '';
        counts[cat] = (counts[cat] || 0) + 1;
      });
      const indices = {};
      return this.selectedDocuments.map((doc, i) => {
        const cat = doc.documentCategory || `書類${i + 1}`;
        if (counts[cat] > 1) {
          indices[cat] = (indices[cat] || 0) + 1;
          return `${cat}-${indices[cat]}`;
        }
        return cat;
      });
    },
    baseDocLabel() {
      return this.docLabels[0] || '書類1';
    },
    unmatchedItemsFlat() {
      // unmatchedItemsByDoc を1つのフラット配列に変換し、ファイル名→明細Noでソート
      const flatItems = [];
      this.unmatchedItemsByDoc.forEach((docData) => {
        docData.items.forEach((item) => {
          flatItems.push({
            ...item,
            documentName: docData.documentName,
            docIndex: docData.docIndex,
          });
        });
      });

      // ファイル名（docIndex）→明細Noでソート
      return flatItems.sort((a, b) => {
        if (a.docIndex !== b.docIndex) {
          return a.docIndex - b.docIndex;
        }
        return a.lineNo - b.lineNo;
      });
    },
    // マッチした明細テーブルのキー（選択行が変わったら再レンダリング）
    matchedTableKey() {
      const lineNo = this.selectedBaseRow ? this.selectedBaseRow.lineNo : 0;
      return 'matched-' + lineNo;
    },
  },
  updated() {
    // 小計行の判定セルにel-tagスタイルを適用
    this.$nextTick(() => {
      const tables = this.$el.querySelectorAll('.el-table');
      tables.forEach((table) => {
        // 判定列（index 9 → nth-child(10)）にel-tagスタイルを適用
        const judgmentCell = table.querySelector(
          '.el-table__footer-wrapper .el-table__footer tbody tr td:nth-child(10) .cell',
        );
        if (judgmentCell && !judgmentCell.querySelector('.el-tag')) {
          const text = judgmentCell.textContent.trim();
          if (text === 'OK' || text === 'NG') {
            const tagSpan = document.createElement('span');
            tagSpan.className = `el-tag el-tag--small el-tag--${text === 'OK' ? 'success' : 'danger'}`;
            tagSpan.textContent = text;
            judgmentCell.innerHTML = '';
            judgmentCell.appendChild(tagSpan);
          }
        }
      });
    });
  },
  methods: {
    getDocumentNumber(documentName) {
      // documentNameから書類番号を取得
      const index = this.selectedDocuments.findIndex(
        (doc) => doc.documentName === documentName,
      );
      return index >= 0 ? this.docLabels[index] || `書類${index + 1}` : '-';
    },
    loadBaseItems() {
      const documents = this.compareData?.documents || [];
      if (documents.length < 1) return;

      const baseDoc = documents[0];
      const items =
        baseDoc.ocrResults?.aiocrTableItems?.goodsLine || [];

      this.baseItems = items.map((item, index) => ({
        ...item,
        lineNo: index + 1,
      }));
    },
    getBaseRowClassName({ row }) {
      if (this.selectedBaseRow && row.lineNo === this.selectedBaseRow.lineNo) {
        return 'selected-base-row';
      }
      return '';
    },
    handleKeyChange() {
      // キーが変更されたら、再度マッチング処理を実行
      if (this.selectedBaseRow) {
        this.isRecalculating = true;
        // ローディング表示を確実に表示するため、$nextTickで非同期処理
        this.$nextTick(() => {
          this.handleBaseRowSelect(this.selectedBaseRow);
          // 再計算完了後、少し遅延させてからローディングを解除（UX向上）
          setTimeout(() => {
            this.isRecalculating = false;
          }, 300);
        });
      }
    },
    handleBaseRowSelect(row) {
      if (!row) {
        this.selectedBaseRow = null;
        this.matchedItems = [];
        this.unmatchedDocuments = [];
        this.unmatchedItemsByDoc = [];
        return;
      }

      this.selectedBaseRow = row;
      const key = this.getItemKey(row, this.keyType);

      if (!key) {
        this.matchedItems = [];
        this.unmatchedDocuments = this.selectedDocuments
          .slice(1)
          .map((doc) => doc.documentName);
        this.unmatchedItemsByDoc = [];
        return;
      }

      // 他の書類から一致する明細を検索
      const documents = this.compareData?.documents || [];
      const matched = [];
      const unmatched = [];
      const unmatchedItems = [];

      for (let i = 1; i < documents.length; i++) {
        const docItems =
          documents[i].ocrResults?.aiocrTableItems?.goodsLine || [];
        const matches = this.findMatchingItems(key, docItems);

        // マッチした明細を追加
        if (matches.length > 0) {
          matches.forEach((item) => {
            matched.push({
              ...item,
              documentName: this.selectedDocuments[i]?.documentName || `Document ${i + 1}`,
              lineNo: docItems.indexOf(item) + 1,
            });
          });
        }

        // マッチしなかった明細を取得（全明細からマッチした明細を除外）
        const unmatchedDocItems = docItems.filter((item) => {
          const itemKey = this.getItemKey(item, this.keyType);
          return itemKey !== key;
        });

        // マッチしなかった明細がある場合、unmatchedItems に追加
        if (unmatchedDocItems.length > 0) {
          unmatchedItems.push({
            docIndex: i + 1,
            documentName: this.selectedDocuments[i]?.documentName || `Document ${i + 1}`,
            items: unmatchedDocItems.map((item) => ({
              ...item,
              lineNo: docItems.indexOf(item) + 1,
            })),
          });
        }

        // 全くマッチしなかった書類を記録
        if (matches.length === 0) {
          unmatched.push(this.selectedDocuments[i].documentName);
        }
      }

      this.matchedItems = matched;
      this.unmatchedDocuments = unmatched;
      this.unmatchedItemsByDoc = unmatchedItems;
    },
    isKeyColumnHighlighted(column) {
      // 各キータイプに応じて、どの列をハイライトするか判定
      const keyMap = {
        goodsShortName: ['goodsShortName'],
        goodsRefNo1: ['goodsRefNo1'],
        goodsShortName_unitPrice: ['goodsShortName', 'unitPrice'],
        goodsShortName_origin: ['goodsShortName', 'origin'],
        goodsShortName_goodsRefNo1: ['goodsShortName', 'goodsRefNo1'],
      };
      const highlightedColumns = keyMap[this.keyType] || [];
      return highlightedColumns.includes(column);
    },
    getItemKey(item, keyType) {
      if (keyType === 'goodsShortName') {
        return item.ocrGoodsShortName || '';
      } else if (keyType === 'goodsRefNo1') {
        return item.ocrGoodsRefNo1 || '';
      } else if (keyType === 'goodsShortName_unitPrice') {
        const goodsName = item.ocrGoodsShortName || '';
        const unitPrice = item.ocrUnitPrice || '';
        const currency = item.ocrImCustomsCurrencies || '';
        return `${goodsName}_${unitPrice}_${currency}`;
      } else if (keyType === 'goodsShortName_origin') {
        const goodsName = item.ocrGoodsShortName || '';
        const origin = item.ocrCountryOfOriginNames || '';
        return `${goodsName}_${origin}`;
      } else if (keyType === 'goodsShortName_goodsRefNo1') {
        const goodsName = item.ocrGoodsShortName || '';
        const goodsRefNo = item.ocrGoodsRefNo1 || '';
        return `${goodsName}_${goodsRefNo}`;
      }
      return '';
    },
    findMatchingItems(key, docItems) {
      return docItems.filter((item) => {
        const itemKey = this.getItemKey(item, this.keyType);
        return itemKey === key;
      });
    },
    getDocumentItemCount(index) {
      const documents = this.compareData?.documents || [];
      if (index >= documents.length) return 0;
      const items =
        documents[index].ocrResults?.aiocrTableItems?.goodsLine || [];
      return items.length;
    },
    formatQuantity(item) {
      const quantity = item.ocrQuantity1 || item.ocrQuantity || '';
      const unit = item.ocrUnit || '';
      if (!quantity && !unit) return '( no result )';
      return `${quantity} ${unit}`.trim();
    },
    formatPrice(item) {
      if (!item || !item.ocrUnitPrice) return '( no result )';
      const currency = item.ocrImCustomsCurrencies || '';
      return `${item.ocrUnitPrice}${currency}`;
    },
    formatAmount(item) {
      if (!item || !item.ocrAmountPerItem) return '( no result )';
      const currency = item.ocrImCustomsCurrencies || item.ocrCurrency || '';
      return `${item.ocrAmountPerItem} ${currency}`.trim();
    },
    getMatchedItemJudgment(item) {
      // 書類1の同じlineNoの明細を取得
      const baseItem = this.baseItems.find(
        (base) => base.lineNo === this.selectedBaseRow.lineNo,
      );
      if (!baseItem) return 'NG';

      // 各フィールドを比較（数量・金額は除外）
      const hasDifference =
        (baseItem.ocrGoodsShortName || '') !== (item.ocrGoodsShortName || '') ||
        (baseItem.ocrGoodsRefNo1 || '') !== (item.ocrGoodsRefNo1 || '') ||
        (baseItem.ocrCountryOfOriginNames || '') !== (item.ocrCountryOfOriginNames || '') ||
        (baseItem.ocrUnitPrice || '') !== (item.ocrUnitPrice || '') ||
        (baseItem.ocrImCustomsCurrencies || '') !== (item.ocrImCustomsCurrencies || '') ||
        (baseItem.ocrImCustomsCurrencies || baseItem.ocrCurrency || '') !==
          (item.ocrImCustomsCurrencies || item.ocrCurrency || '');
      return hasDifference ? 'NG' : 'OK';
    },
    getMatchedItemDifference(item) {
      // 書類1の同じlineNoの明細を取得
      const baseItem = this.baseItems.find(
        (base) => base.lineNo === this.selectedBaseRow.lineNo,
      );
      if (!baseItem) return 'マッチしない';

      // 各フィールドを比較（列順: 商品名→商品コード→原産国→数量の単位→単価→単価の通貨→金額の通貨）
      const differences = [];
      if ((baseItem.ocrGoodsShortName || '') !== (item.ocrGoodsShortName || '')) {
        differences.push('商品名');
      }
      if ((baseItem.ocrGoodsRefNo1 || '') !== (item.ocrGoodsRefNo1 || '')) {
        differences.push('商品コード');
      }
      if (
        (baseItem.ocrCountryOfOriginNames || '') !==
        (item.ocrCountryOfOriginNames || '')
      ) {
        differences.push('原産国');
      }
      if ((baseItem.ocrUnitPrice || '') !== (item.ocrUnitPrice || '')) {
        const p1 = parseFloat(baseItem.ocrUnitPrice);
        const p2 = parseFloat(item.ocrUnitPrice);
        if (!isNaN(p1) && !isNaN(p2)) {
          const diff = p2 - p1;
          const sign = diff >= 0 ? '+' : '';
          differences.push(
            `単価（差分：${sign}${diff.toLocaleString()}）`,
          );
        } else {
          differences.push('単価');
        }
      }
      if (
        (baseItem.ocrImCustomsCurrencies || '') !==
        (item.ocrImCustomsCurrencies || '')
      ) {
        differences.push('単価の通貨');
      }
      if (
        (baseItem.ocrImCustomsCurrencies || baseItem.ocrCurrency || '') !==
        (item.ocrImCustomsCurrencies || item.ocrCurrency || '')
      ) {
        differences.push('金額の通貨');
      }

      return differences.length > 0 ? differences.join(', ') : '-';
    },
    getUnmatchedItemDifference(item) {
      // 書類1の選択明細を取得
      const baseItem = this.baseItems.find(
        (base) => base.lineNo === this.selectedBaseRow.lineNo,
      );
      if (!baseItem) return '比較対象なし';

      // 各フィールドを比較（数量を除外）
      const differences = [];
      if ((baseItem.ocrGoodsShortName || '') !== (item.ocrGoodsShortName || '')) {
        differences.push('商品名');
      }
      if ((baseItem.ocrGoodsRefNo1 || '') !== (item.ocrGoodsRefNo1 || '')) {
        differences.push('商品コード');
      }
      if ((baseItem.ocrCountryOfOriginNames || '') !== (item.ocrCountryOfOriginNames || '')) {
        differences.push('原産国');
      }
      if ((baseItem.ocrUnitPrice || '') !== (item.ocrUnitPrice || '')) {
        const p1 = parseFloat(baseItem.ocrUnitPrice);
        const p2 = parseFloat(item.ocrUnitPrice);
        if (!isNaN(p1) && !isNaN(p2)) {
          const diff = p2 - p1;
          const sign = diff >= 0 ? '+' : '';
          differences.push(
            `単価（差分：${sign}${diff.toLocaleString()}）`,
          );
        } else {
          differences.push('単価');
        }
      }
      if (
        (baseItem.ocrImCustomsCurrencies || '') !==
        (item.ocrImCustomsCurrencies || '')
      ) {
        differences.push('単価の通貨');
      }

      return differences.length > 0 ? differences.join(', ') : '-';
    },
    getMatchedItemsQuantityTotal() {
      // 数量の合計を計算
      // 単位が異なる可能性があるため、単位別に集計
      const quantityByUnit = {};

      this.matchedItems.forEach((item) => {
        const quantityStr = item.ocrQuantity1 || item.ocrQuantity || item.quantity1 || '0';
        const quantity = parseFloat(quantityStr);
        const unit = item.ocrUnit || item.unit || '';

        if (!isNaN(quantity) && quantity > 0) {
          if (!quantityByUnit[unit]) {
            quantityByUnit[unit] = 0;
          }
          quantityByUnit[unit] += quantity;
        }
      });

      // 単位別に合計を表示（改行区切り）
      const totals = Object.entries(quantityByUnit)
        .filter(([, total]) => total > 0)
        .map(([unit, total]) => `${total.toFixed(1)} ${unit}`.trim());

      return totals.length > 0 ? totals.join('\n') : '-';
    },
    getMatchedItemsAmountTotal() {
      // 金額の合計を計算
      // 通貨が異なる可能性があるため、通貨別に集計
      const amountByCurrency = {};

      this.matchedItems.forEach((item) => {
        const amount = parseFloat(item.ocrAmountPerItem || 0);
        const currency = item.ocrImCustomsCurrencies || item.ocrCurrency || '';

        if (!isNaN(amount) && amount > 0) {
          if (!amountByCurrency[currency]) {
            amountByCurrency[currency] = 0;
          }
          amountByCurrency[currency] += amount;
        }
      });

      // 通貨別に合計を表示（改行区切り）
      const totals = Object.entries(amountByCurrency)
        .filter(([, total]) => total > 0)
        .map(([currency, total]) => `${total.toFixed(2)} ${currency}`.trim());

      return totals.length > 0 ? totals.join('\n') : '-';
    },
    downloadMatchedItemsTsv() {
      // マッチした明細のTSVダウンロード
      const headers = [
        '書類No',
        'ファイル名',
        '明細No',
        '商品名',
        '商品コード',
        '原産国',
        '数量',
        '単価',
        '金額',
        '判定',
        '差分',
      ];
      const rows = [headers.join('\t')];

      this.matchedItems.forEach((item) => {
        const row = [
          this.getDocumentNumber(item.documentName),
          item.documentName || '',
          item.lineNo || '',
          item.ocrGoodsShortName || '',
          item.ocrGoodsRefNo1 || '',
          item.ocrCountryOfOriginNames || '',
          this.formatQuantity(item),
          this.formatPrice(item),
          this.formatAmount(item),
          this.getMatchedItemJudgment(item),
          this.getMatchedItemDifference(item),
        ];
        rows.push(row.join('\t'));
      });

      // 小計行の判定・差分を算出
      const baseAmount = parseFloat(
        this.selectedBaseRow?.ocrAmountPerItem || 0,
      );
      const baseCurrency =
        this.selectedBaseRow?.ocrImCustomsCurrencies ||
        this.selectedBaseRow?.ocrCurrency ||
        '';
      const baseQuantity = parseFloat(
        this.selectedBaseRow?.ocrQuantity1 ||
          this.selectedBaseRow?.ocrQuantity ||
          0,
      );
      const baseUnit = this.selectedBaseRow?.ocrUnit || '';

      const amountByCurrency = {};
      const quantityByUnit = {};
      this.matchedItems.forEach((item) => {
        const amount = parseFloat(item.ocrAmountPerItem || 0);
        const currency =
          item.ocrImCustomsCurrencies || item.ocrCurrency || '';
        if (!isNaN(amount) && amount > 0) {
          amountByCurrency[currency] =
            (amountByCurrency[currency] || 0) + amount;
        }
        const qty = parseFloat(
          item.ocrQuantity1 || item.ocrQuantity || 0,
        );
        const unit = item.ocrUnit || '';
        if (!isNaN(qty) && qty > 0) {
          quantityByUnit[unit] = (quantityByUnit[unit] || 0) + qty;
        }
      });

      const matchedTotal = amountByCurrency[baseCurrency] || 0;
      const isAmountMatch =
        Math.abs(baseAmount - matchedTotal) < 0.01;
      const matchedQuantity = baseUnit
        ? quantityByUnit[baseUnit] || 0
        : null;
      const isQuantityMatch =
        matchedQuantity !== null
          ? Math.abs(baseQuantity - matchedQuantity) < 0.01
          : true;

      // 金額・数量の両方が一致で OK（画面側と同じロジック）
      const isAllMatch = isAmountMatch && isQuantityMatch;

      // 単位・通貨の不一致を検出
      const matchedUnits = [
        ...new Set(this.matchedItems.map((item) => item.ocrUnit || '')),
      ];
      const hasUnitMismatch =
        matchedUnits.length > 1 ||
        (matchedUnits.length === 1 && matchedUnits[0] !== baseUnit);

      const matchedCurrencies = [
        ...new Set(
          this.matchedItems.map(
            (item) =>
              item.ocrImCustomsCurrencies || item.ocrCurrency || '',
          ),
        ),
      ];
      const hasCurrencyMismatch =
        matchedCurrencies.length > 1 ||
        (matchedCurrencies.length === 1 &&
          matchedCurrencies[0] !== baseCurrency);

      // マッチした明細の書類ラベルを取得（画面の getMatchedItemsSummaries と同じロジック）
      const matchedDocNames = [
        ...new Set(this.matchedItems.map((item) => item.documentName)),
      ];
      const matchedDocs = matchedDocNames.map((name) => {
        const idx = this.selectedDocuments.findIndex(
          (doc) => doc.documentName === name,
        );
        return {
          label: idx >= 0 ? this.docLabels[idx] : name,
          category:
            idx >= 0
              ? this.selectedDocuments[idx].documentCategory || ''
              : '',
        };
      });
      const uniqueCategories = [
        ...new Set(matchedDocs.map((d) => d.category)),
      ];
      const matchedDocLabel =
        matchedDocs.length > 1 &&
        uniqueCategories.length === 1 &&
        uniqueCategories[0]
          ? `${uniqueCategories[0]} の合計`
          : matchedDocs.map((d) => d.label).join(', ');

      // 差分テキスト（TSV用: 改行をスペースに変換）
      const diffParts = [];
      if (hasUnitMismatch) {
        diffParts.push(
          '数量: 単位が異なる数量が存在するため、比較できません',
        );
      } else if (!isQuantityMatch && matchedQuantity !== null) {
        diffParts.push(
          `数量: ${this.baseDocLabel}: ${baseQuantity.toFixed(1)} ${baseUnit}, ${matchedDocLabel}: ${matchedQuantity.toFixed(1)} ${baseUnit}`,
        );
      }
      if (hasCurrencyMismatch) {
        diffParts.push(
          '金額: 通貨が異なる金額が存在するため、比較できません',
        );
      } else if (!isAmountMatch) {
        diffParts.push(
          `金額: ${this.baseDocLabel}: ${baseAmount.toFixed(2)} ${baseCurrency}, ${matchedDocLabel}: ${matchedTotal.toFixed(2)} ${baseCurrency}`,
        );
      }
      const diffText =
        diffParts.length > 0
          ? `差異があります / ${diffParts.join(' / ')}`
          : '一致しています';

      // 小計行を追加
      const summaryRow = [
        '',
        '小計',
        '',
        '',
        '',
        '',
        this.getMatchedItemsQuantityTotal().replace(/\n/g, ' '),
        '',
        this.getMatchedItemsAmountTotal().replace(/\n/g, ' '),
        isAllMatch ? 'OK' : 'NG',
        diffText,
      ];
      rows.push(summaryRow.join('\t'));

      const tsvContent = rows.join('\n');
      const blob = new Blob([tsvContent], {
        type: 'text/tab-separated-values',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:]/g, '')
        .split('.')[0]
        .replace('T', '_');
      link.download = `matched_items_${timestamp}.tsv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadUnmatchedItemsTsv() {
      // マッチしない明細のTSVダウンロード（書類No、ファイル名列を先頭に追加）
      const headers = [
        '書類No',
        'ファイル名',
        '明細No',
        '商品名',
        '商品コード',
        '原産国',
        '数量',
        '単価',
        '金額',
        '判定',
        '差分',
      ];
      const rows = [headers.join('\t')];

      this.unmatchedItemsByDoc.forEach((docData) => {
        docData.items.forEach((item) => {
          const row = [
            this.getDocumentNumber(docData.documentName),
            docData.documentName || '',
            item.lineNo || '',
            item.ocrGoodsShortName || '',
            item.ocrGoodsRefNo1 || '',
            item.ocrCountryOfOriginNames || '',
            this.formatQuantity(item),
            this.formatPrice(item),
            this.formatAmount(item),
            'NG',
            this.getUnmatchedItemDifference(item),
          ];
          rows.push(row.join('\t'));
        });
      });

      const tsvContent = rows.join('\n');
      const blob = new Blob([tsvContent], { type: 'text/tab-separated-values' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0].replace('T', '_');
      link.download = `unmatched_items_${timestamp}.tsv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    getMatchedItemsSummaries(param) {
      const { columns } = param;
      const sums = [];

      // 書類1の選択された明細の金額・通貨・数量・単位を取得
      const baseAmount = parseFloat(this.selectedBaseRow?.ocrAmountPerItem || 0);
      const baseCurrency = this.selectedBaseRow?.ocrImCustomsCurrencies ||
                           this.selectedBaseRow?.ocrCurrency || '';
      const baseQuantity = parseFloat(
        this.selectedBaseRow?.ocrQuantity1 || this.selectedBaseRow?.ocrQuantity || 0,
      );
      const baseUnit = this.selectedBaseRow?.ocrUnit || '';

      // マッチした明細の通貨別金額合計を計算
      const amountByCurrency = {};
      this.matchedItems.forEach((item) => {
        const amount = parseFloat(item.ocrAmountPerItem || 0);
        const currency = item.ocrImCustomsCurrencies || item.ocrCurrency || '';
        if (!isNaN(amount) && amount > 0) {
          amountByCurrency[currency] = (amountByCurrency[currency] || 0) + amount;
        }
      });

      // マッチした明細の単位別数量合計を計算
      const quantityByUnit = {};
      this.matchedItems.forEach((item) => {
        const qty = parseFloat(item.ocrQuantity1 || item.ocrQuantity || 0);
        const unit = item.ocrUnit || '';
        if (!isNaN(qty) && qty > 0) {
          quantityByUnit[unit] = (quantityByUnit[unit] || 0) + qty;
        }
      });

      // 金額判定
      const matchedTotal = amountByCurrency[baseCurrency] || 0;
      const isAmountMatch = Math.abs(baseAmount - matchedTotal) < 0.01;

      // 数量判定（基準の単位が特定できる場合のみ）
      const matchedQuantity = baseUnit ? (quantityByUnit[baseUnit] || 0) : null;
      const isQuantityMatch = matchedQuantity !== null
        ? Math.abs(baseQuantity - matchedQuantity) < 0.01
        : true;

      // 金額・数量の両方が一致で OK
      const isAllMatch = isAmountMatch && isQuantityMatch;

      // マッチした明細の書類ラベルを取得
      const matchedDocNames = [
        ...new Set(this.matchedItems.map((item) => item.documentName)),
      ];
      const matchedDocs = matchedDocNames.map((name) => {
        const idx = this.selectedDocuments.findIndex(
          (doc) => doc.documentName === name,
        );
        return {
          label: idx >= 0 ? this.docLabels[idx] : name,
          category: idx >= 0 ? (this.selectedDocuments[idx].documentCategory || '') : '',
        };
      });

      // 複数書類かつ全て同じカテゴリ → "{カテゴリ} の合計"
      const uniqueCategories = [...new Set(matchedDocs.map((d) => d.category))];
      const matchedDocLabel =
        matchedDocs.length > 1 && uniqueCategories.length === 1 && uniqueCategories[0]
          ? `${uniqueCategories[0]} の合計`
          : matchedDocs.map((d) => d.label).join(', ');

      // 単位・通貨の不一致を検出
      const matchedUnits = [
        ...new Set(this.matchedItems.map((item) => item.ocrUnit || '')),
      ];
      const hasUnitMismatch =
        matchedUnits.length > 1 ||
        (matchedUnits.length === 1 && matchedUnits[0] !== baseUnit);

      const matchedCurrencies = [
        ...new Set(
          this.matchedItems.map(
            (item) =>
              item.ocrImCustomsCurrencies || item.ocrCurrency || '',
          ),
        ),
      ];
      const hasCurrencyMismatch =
        matchedCurrencies.length > 1 ||
        (matchedCurrencies.length === 1 &&
          matchedCurrencies[0] !== baseCurrency);

      // 差分テキストを生成
      const diffLines = [];
      if (hasUnitMismatch) {
        diffLines.push(
          '数量: 単位が異なる数量が存在するため、比較できません',
        );
      } else if (!isQuantityMatch && matchedQuantity !== null) {
        diffLines.push(
          `数量: \n${this.baseDocLabel}: ${baseQuantity.toFixed(1)} ${baseUnit}, \n${matchedDocLabel}: ${matchedQuantity.toFixed(1)} ${baseUnit}`,
        );
      }
      if (hasCurrencyMismatch) {
        diffLines.push(
          '金額: 通貨が異なる金額が存在するため、比較できません',
        );
      } else if (!isAmountMatch) {
        diffLines.push(
          `金額: \n${this.baseDocLabel}: ${baseAmount.toFixed(2)} ${baseCurrency}, \n${matchedDocLabel}: ${matchedTotal.toFixed(2)} ${baseCurrency}`,
        );
      }
      const diffText = diffLines.length > 0
        ? `差異があります\n${diffLines.join('\n\n')}`
        : '一致しています';

      columns.forEach((_, index) => {
        if (index === 0) {
          // 書類No列
          sums[index] = '';
        } else if (index === 1) {
          // ファイル名列
          sums[index] = '小計';
        } else if (index === 6) {
          // 数量列
          sums[index] = this.getMatchedItemsQuantityTotal();
        } else if (index === 8) {
          // 金額列
          sums[index] = this.getMatchedItemsAmountTotal();
        } else if (index === 9) {
          // 判定列
          sums[index] = isAllMatch ? 'OK' : 'NG';
        } else if (index === 10) {
          // 差分列
          sums[index] = diffText;
        } else {
          // その他の列は空白
          sums[index] = '';
        }
      });

      return sums;
    },
    getUnmatchedItemsSummaries(param, docData) {
      const { columns } = param;
      const sums = [];
      const items = docData.items || [];

      columns.forEach((_, index) => {
        if (index === 0) {
          // 明細No列
          sums[index] = '小計';
        } else if (index === 3) {
          // 数量列 - 単位別の合計を計算
          const quantityByUnit = {};
          items.forEach((item) => {
            const quantityStr = item.ocrQuantity1 || item.ocrQuantity || '0';
            const quantity = parseFloat(quantityStr);
            const unit = item.ocrUnit || item.unit || '';
            if (!isNaN(quantity) && quantity > 0) {
              if (!quantityByUnit[unit]) {
                quantityByUnit[unit] = 0;
              }
              quantityByUnit[unit] += quantity;
            }
          });
          const totals = Object.entries(quantityByUnit)
            .filter(([, total]) => total > 0)
            .map(([unit, total]) => `${total.toFixed(1)} ${unit}`.trim());
          sums[index] = totals.length > 0 ? totals.join('\n') : '-';
        } else if (index === 5) {
          // 金額列 - 合計を計算
          const total = items.reduce((sum, item) => {
            const amount = parseFloat(item.ocrAmountPerItem || 0);
            return sum + amount;
          }, 0);
          const currency = items.length > 0 ? items[0].ocrCurrency || '' : '';
          sums[index] = total > 0 ? `${total.toFixed(2)} ${currency}`.trim() : '-';
        } else {
          // その他の列は空白
          sums[index] = '';
        }
      });

      return sums;
    },
    getUnmatchedItemsSummariesFlat(param) {
      const { columns } = param;
      const sums = [];
      const items = this.unmatchedItemsFlat || [];

      columns.forEach((_, index) => {
        if (index === 0) {
          // ファイル名列
          sums[index] = '小計';
        } else if (index === 4) {
          // 数量列 - 単位別の合計を計算
          const quantityByUnit = {};
          items.forEach((item) => {
            const quantityStr = item.ocrQuantity1 || item.ocrQuantity || '0';
            const quantity = parseFloat(quantityStr);
            const unit = item.ocrUnit || item.unit || '';
            if (!isNaN(quantity) && quantity > 0) {
              if (!quantityByUnit[unit]) {
                quantityByUnit[unit] = 0;
              }
              quantityByUnit[unit] += quantity;
            }
          });
          const totals = Object.entries(quantityByUnit)
            .filter(([, total]) => total > 0)
            .map(([unit, total]) => `${total.toFixed(1)} ${unit}`.trim());
          sums[index] = totals.length > 0 ? totals.join('\n') : '-';
        } else if (index === 6) {
          // 金額列 - 合計を計算（金額と原産国の順序を入れ替えたため index 7 → 6）
          const total = items.reduce((sum, item) => {
            const amount = parseFloat(item.ocrAmountPerItem || 0);
            return sum + amount;
          }, 0);
          const currency =
            items.length > 0
              ? items[0].ocrImCustomsCurrencies || items[0].ocrCurrency || ''
              : '';
          sums[index] =
            total > 0 ? `${total.toFixed(2)} ${currency}`.trim() : '-';
        } else {
          // その他の列は空白
          sums[index] = '';
        }
      });

      return sums;
    },
  },
};
</script>

<style scoped lang="scss">
.key-selection {
  display: flex;
  align-items: center;
}

.base-table-container {
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
}

.match-result-container {
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  height: auto;
  overflow: visible;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.table-title {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

.no-match-message {
  padding: 20px;
  text-align: center;
  color: #f56c6c;
  font-size: 14px;
  background-color: #fef0f0;
  border-radius: 4px;
}

.no-selection-message {
  padding: 40px;
  text-align: center;
  color: #909399;
  font-size: 14px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.text-info {
  margin-right: 8px;
  font-size: 16px;
}

.text-warning {
  margin-right: 8px;
}

.unmatched-docs {
  padding: 10px;
  background-color: #fef0f0;
  border-left: 3px solid #f56c6c;
  border-radius: 4px;
}

// キー列のヘッダーのみに背景色を適用（小計行には適用しない）
::v-deep .el-table__header .key-column-header {
  background-color: #ecf5ff !important;
  color: #222 !important;
}

.text-error {
  color: #f56c6c;
  font-weight: bold;
}

.text-bold {
  font-weight: bold;
}

.unmatched-result-container {
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin-top: 15px;
}

.unmatched-items-details {
  margin-top: 10px;
}

.unmatched-doc-items {
  margin-bottom: 15px;
}

.mb-3 {
  margin-bottom: 15px;
}

.doc-subtitle {
  margin: 0 0 10px 0;
  font-size: 13px;
  font-weight: bold;
  color: #606266;
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-left: 3px solid #409eff;
  border-radius: 2px;
}

::v-deep .selected-base-row {
  font-weight: bold;
}

// テーブルの列幅を固定
::v-deep .el-table {
  table-layout: fixed;
}

::v-deep .el-table__body,
::v-deep .el-table__header,
::v-deep .el-table__footer {
  table-layout: fixed;
}

// 小計行の幅を100%に強制（Element UIが動的に設定する固定幅を上書き）
::v-deep .el-table__footer {
  width: 100% !important;
}


// 小計行の背景色を透明に、改行を表示
::v-deep .el-table__footer .cell {
  background-color: transparent !important;
  white-space: pre-line !important;
}
</style>
